import { ref } from 'vue'
import type { Ref } from 'vue'
import {
  listEventMedia,
  getEventMedia,
  uploadEventSignatureImage,
  deleteEventSignatureImage,
} from '@/api/eventMedia'
import type { EventMedia } from '@/types/eventMedia'
import { useEvents } from '@/composables/useEvents'

export const MAX_SIGNATURE_IMAGES = 5

export function useEventSignatureMedia(eventSlug: string): {
  images: Ref<EventMedia[]>
  loading: Ref<boolean>
  error: Ref<string | null>
  uploadProgress: Ref<Record<string, number>>
  fetchImages: () => Promise<void>
  uploadImage: (file: File) => Promise<EventMedia>
  deleteImage: (mediaId: number) => Promise<void>
  stopAllPolling: () => void
} {
  const { lastSavedAt } = useEvents()

  const images = ref<EventMedia[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)
  const uploadProgress = ref<Record<string, number>>({})
  const pollHandles = ref<Map<number, ReturnType<typeof setInterval>>>(new Map())

  async function fetchImages(): Promise<void> {
    loading.value = true
    error.value = null
    try {
      const results = await listEventMedia(eventSlug, 'signature')
      images.value = results

      for (const img of results) {
        if (img.status === 'pending' || img.status === 'processing') {
          startPolling(img.id)
        }
      }
    } catch {
      error.value = 'common.error_generic'
    } finally {
      loading.value = false
    }
  }

  async function uploadImage(file: File): Promise<EventMedia> {
    const tempKey = `upload-${Date.now()}`
    uploadProgress.value = { ...uploadProgress.value, [tempKey]: 0 }

    const uploaded = await uploadEventSignatureImage(eventSlug, file, (pct) => {
      uploadProgress.value = { ...uploadProgress.value, [tempKey]: pct }
    })

    const { [tempKey]: _, ...rest } = uploadProgress.value
    uploadProgress.value = rest

    images.value = [...images.value, uploaded]
    lastSavedAt.value = new Date()

    if (uploaded.status === 'pending' || uploaded.status === 'processing') {
      startPolling(uploaded.id)
    }

    return uploaded
  }

  async function deleteImage(mediaId: number): Promise<void> {
    stopPolling(mediaId)
    await deleteEventSignatureImage(eventSlug, mediaId)
    images.value = images.value.filter((img) => img.id !== mediaId)
    lastSavedAt.value = new Date()
  }

  function startPolling(mediaId: number): void {
    stopPolling(mediaId)

    const handle = setInterval(async () => {
      try {
        const media = await getEventMedia(eventSlug, mediaId)
        images.value = images.value.map((img) => (img.id === mediaId ? media : img))

        if (media.status === 'ready' || media.status === 'failed') {
          stopPolling(mediaId)

          if (media.status === 'ready') {
            lastSavedAt.value = new Date()
          }
        }
      } catch {
        stopPolling(mediaId)
      }
    }, 3000)

    pollHandles.value.set(mediaId, handle)
  }

  function stopPolling(mediaId: number): void {
    const handle = pollHandles.value.get(mediaId)
    if (handle !== undefined) {
      clearInterval(handle)
      pollHandles.value.delete(mediaId)
    }
  }

  function stopAllPolling(): void {
    for (const [id] of pollHandles.value) {
      stopPolling(id)
    }
  }

  return {
    images,
    loading,
    error,
    uploadProgress,
    fetchImages,
    uploadImage,
    deleteImage,
    stopAllPolling,
  }
}
